/**
 * API Service for Auxilio Finance
 * Handles all HTTP requests to the backend
 */

import axios, { AxiosInstance, AxiosError } from 'axios';

// Determine API base URL
const getBaseURL = (): string => {
  // In production (served via nginx), use /api prefix
  if (window.location.port === '80' || window.location.port === '3002' || window.location.port === '') {
    return '/api';
  }
  // In development, connect directly to backend
  return 'http://localhost:8002';
};

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token and language preference
apiClient.interceptors.request.use(
  (config) => {
    const authData = localStorage.getItem('auxilio-finance-auth');
    if (authData) {
      try {
        const { state } = JSON.parse(authData);
        if (state?.token) {
          config.headers.Authorization = `Bearer ${state.token}`;
        }
      } catch (e) {
        console.error('Error parsing auth data:', e);
      }
    }

    // Add language preference from i18next
    const language = localStorage.getItem('i18nextLng') || 'en';
    config.headers['Accept-Language'] = language;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Clear auth and redirect to login
      localStorage.removeItem('auxilio-finance-auth');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export default apiClient;

// Auth API functions
export const authAPI = {
  login: async (credentials: { username: string; email: string; password: string }) => {
    const response = await apiClient.post('/auth/login', credentials);
    return response.data;
  },

  register: async (data: { username: string; email: string; password: string }) => {
    const response = await apiClient.post('/auth/register', data);
    return response.data;
  },

  verifyEmail: async (email: string, code: string) => {
    const response = await apiClient.post('/auth/verify-email', { email, code });
    return response.data;
  },

  resendVerificationEmail: async (email: string) => {
    const response = await apiClient.post('/auth/resend-verification-email', { email });
    return response.data;
  },

  resetPassword: async (email: string, newPassword: string) => {
    const response = await apiClient.post('/auth/reset-password', { email, new_password: newPassword });
    return response.data;
  },

  logout: async () => {
    const response = await apiClient.post('/auth/logout');
    return response.data;
  },
};

// Glossary API functions
export const glossaryAPI = {
  getTerms: async () => {
    const response = await apiClient.get('/financial-glossary/terms');
    return response.data;
  },

  getCategories: async () => {
    const response = await apiClient.get('/financial-glossary/categories');
    return response.data;
  },

  searchTerms: async (query: string) => {
    const response = await apiClient.get(`/financial-glossary/search?q=${encodeURIComponent(query)}`);
    return response.data;
  },

  explainTerm: async (term: string, simpleMode: boolean = false) => {
    const response = await apiClient.post('/financial-glossary/explain', { term, simple_mode: simpleMode });
    return response.data;
  },

  addUserTerm: async (term: string, definition: string, category?: string) => {
    const response = await apiClient.post('/financial-glossary/user-terms', { term, definition, category });
    return response.data;
  },

  getUserTerms: async () => {
    const response = await apiClient.get('/financial-glossary/user-terms');
    return response.data;
  },

  deleteUserTerm: async (termId: string) => {
    const response = await apiClient.delete(`/financial-glossary/user-terms/${termId}`);
    return response.data;
  },

  suggestCategory: async (term: string, definition: string) => {
    const response = await apiClient.post('/financial-glossary/suggest-category', { term, definition });
    return response.data;
  },
};

// Calculator API functions
export const calculatorAPI = {
  calculateCompoundInterest: async (data: {
    principal: number;
    rate: number;
    time: number;
    frequency: string;
  }) => {
    const response = await apiClient.post('/calculators/compound-interest', data);
    return response.data;
  },

  calculateLoan: async (data: {
    principal: number;
    rate: number;
    term: number;
  }) => {
    const response = await apiClient.post('/calculators/loan', data);
    return response.data;
  },

  calculateRetirement: async (data: {
    currentAge: number;
    retirementAge: number;
    currentSavings: number;
    monthlyContribution: number;
    expectedReturn: number;
  }) => {
    const response = await apiClient.post('/calculators/retirement', data);
    return response.data;
  },

  calculateEmergencyFund: async (data: {
    monthlyExpenses: number;
    months: number;
  }) => {
    const response = await apiClient.post('/calculators/emergency-fund', data);
    return response.data;
  },

  calculateDebtPayoff: async (data: {
    balance: number;
    rate: number;
    payment: number;
  }) => {
    const response = await apiClient.post('/calculators/debt-payoff', data);
    return response.data;
  },

  calculateInvestmentGrowth: async (data: {
    initialInvestment: number;
    monthlyContribution: number;
    years: number;
    expectedReturn: number;
  }) => {
    const response = await apiClient.post('/calculators/investment-growth', data);
    return response.data;
  },
};

// Financial Questions API functions
export const questionsAPI = {
  askQuestion: async (data: {
    category: string;
    question: string;
    simpleMode?: boolean;
  }) => {
    const response = await apiClient.post('/financial-questions/ask', data);
    return response.data;
  },

  getCategories: async () => {
    const response = await apiClient.get('/financial-questions/categories');
    return response.data;
  },

  getHistory: async () => {
    const response = await apiClient.get('/financial-questions/history');
    return response.data;
  },

  deleteFromHistory: async (questionId: string) => {
    const response = await apiClient.delete(`/financial-questions/history/${questionId}`);
    return response.data;
  },
};

// Feedback API
export const feedbackAPI = {
  submit: async (data: {
    rating: number;
    category: string;
    feature_area?: string | null;
    message: string;
    email?: string | null;
  }) => {
    const response = await apiClient.post('/feedback/submit', data);
    return response.data;
  },

  getCategories: async () => {
    const response = await apiClient.get('/feedback/categories');
    return response.data;
  },

  getFeatureAreas: async () => {
    const response = await apiClient.get('/feedback/feature-areas');
    return response.data;
  },
};

// Export API
export interface ExportRequest {
  content: string;
  title: string;
  format: 'docx' | 'pdf' | 'txt';
}

export const exportAPI = {
  exportDocument: async (request: ExportRequest): Promise<Blob> => {
    const response = await apiClient.post('/export/document', request, {
      responseType: 'blob',
    });
    return response.data;
  },
};
